import React, { useState, useEffect, useMemo, useCallback } from 'react';
import brandPartnerService from '../../services/brandPartnerService';
import { getImageUrl } from '../../utils/imageUrl';
import './BrandMarquee.css';

export const BrandMarquee = () => {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [, setError] = useState(null);
    const [failedImages, setFailedImages] = useState({});
    const [paused] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const fetchBrandPartners = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await brandPartnerService.getAll();

                if (!isMounted) return;

                if (Array.isArray(data) && data.length > 0) {
                    // Filter active partners and sort by display_order (ascending)
                    const activeList = data
                        .filter(
                            (item) =>
                                item &&
                                item.is_active !== false &&
                                item.isActive !== false &&
                                item.status !== 'inactive'
                        )
                        .sort((a, b) => {
                            const orderA = Number.isFinite(Number(a.display_order ?? a.order))
                                ? Number(a.display_order ?? a.order)
                                : 999;
                            const orderB = Number.isFinite(Number(b.display_order ?? b.order))
                                ? Number(b.display_order ?? b.order)
                                : 999;
                            return orderA - orderB;
                        });

                    setPartners(activeList);
                } else {
                    setPartners([]);
                }
            } catch (err) {
                if (!isMounted) return;
                console.warn(
                    '[BrandMarquee] Unable to reach backend brand-partners API:',
                    err?.message || err
                );
                setError(err);
                setPartners([]);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchBrandPartners();

        return () => {
            isMounted = false;
        };
    }, []);

    const handleImageError = useCallback((identifier) => {
        setFailedImages((prev) => ({ ...prev, [identifier]: true }));
    }, []);

    // Ensure enough tiles to span wide screens seamlessly for the CSS infinite loop
    const displayList = useMemo(() => {
        if (!partners.length) return [];
        if (partners.length >= 8) return partners;
        const multiplier = Math.ceil(8 / partners.length);
        const repeated = [];
        for (let i = 0; i < multiplier; i++) {
            repeated.push(...partners);
        }
        return repeated;
    }, [partners]);

    // If loading for the first time without any cached/fallback data, render elegant skeleton track
    if (loading && partners.length === 0) {
        return (
            <section className="brand-section" aria-labelledby="brand-heading">
                <div className="container brand-header-block">
                    <div className="brand-header-center">
                        <span className="section-eyebrow brand-eyebrow">OFFICIAL AUTHORIZED PARTNERS</span>
                        <h2 id="brand-heading" className="brand-main-title">
                            Authorized Brand Partners
                        </h2>
                        <p className="brand-main-subtitle">
                            100% Genuine Box-Packed Appliances with Official Brand Warranties
                        </p>
                    </div>
                </div>
                <div className="brand-window">
                    <div className="brand-track">
                        {[0, 1].map((copy) => (
                            <ul key={copy} className="brand-group" aria-hidden={copy === 1 ? true : undefined}>
                                {Array.from({ length: 8 }).map((_, idx) => (
                                    <li key={`skeleton-${copy}-${idx}`} className="brand-item brand-item--skeleton">
                                        <div className="brand-skeleton-bone" />
                                    </li>
                                ))}
                            </ul>
                        ))}
                    </div>
                </div>
            </section>
        );
    }

    // If API returned 0 configured partners, gracefully do not render an empty section
    if (!loading && partners.length === 0) {
        return null;
    }

    return (
        <section className="brand-section" aria-labelledby="brand-heading">
            <div className="container brand-header-block">
                <div className="brand-header-center">
                    <span className="section-eyebrow brand-eyebrow">OFFICIAL AUTHORIZED PARTNERS</span>
                    <h2 id="brand-heading" className="brand-main-title">
                        Authorized Brand Partners
                    </h2>
                    <p className="brand-main-subtitle">
                        100% Genuine Box-Packed Appliances with Official Brand Warranties
                    </p>
                </div>
            </div>
            <div className={`brand-window ${paused ? 'is-paused' : ''}`}>
                <div className="brand-track">
                    {[0, 1].map((copy) => (
                        <ul key={copy} className="brand-group" aria-hidden={copy === 1 ? true : undefined}>
                            {displayList.map((brand, idx) => {
                                const brandId = brand._id || brand.id || brand.name || `brand-${idx}`;
                                const brandName = brand.name || 'Brand Partner';
                                const brandSlug = brandName.toLowerCase().replace(/[^a-z0-9]/g, '-');
                                const rawLogo = brand.logo || brand.image || brand.logo_url || brand.logoUrl;
                                const logoUrl = getImageUrl(rawLogo);
                                const rawUrl = brand.website_url || brand.url || brand.link;
                                const hasValidUrl = Boolean(
                                    rawUrl &&
                                    rawUrl !== '#' &&
                                    (rawUrl.startsWith('http://') || rawUrl.startsWith('https://') || rawUrl.startsWith('//'))
                                );
                                const isImgFailed = Boolean(failedImages[brandId]);

                                const tileContent = (
                                    <>
                                        {logoUrl && !isImgFailed ? (
                                            <img
                                                src={logoUrl}
                                                alt={copy === 0 ? brandName : ''}
                                                className={`brand-logo-img brand-logo-img--${brandSlug}`}
                                                loading="lazy"
                                                onError={() => handleImageError(brandId)}
                                            />
                                        ) : (
                                            <span className="brand-fallback-name">{brandName}</span>
                                        )}
                                    </>
                                );

                                return (
                                    <li
                                        key={`${brandId}-${copy}-${idx}`}
                                        className="brand-item"
                                    >
                                        {hasValidUrl ? (
                                            <a
                                                href={rawUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="brand-image"
                                                title={`Visit ${brandName} official website`}
                                                aria-label={`${brandName} official website`}
                                            >
                                                {tileContent}
                                            </a>
                                        ) : (
                                            <div className="brand-image" title={brandName}>
                                                {tileContent}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default BrandMarquee;

